import React, { useState, useRef, useEffect, useContext } from 'react'
import { differenceInSeconds, format } from 'date-fns'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FiAlertTriangle, FiCheckCircle, FiClock, FiEye, FiLink, FiPower, FiRotateCw, FiServer, FiXCircle } from 'react-icons/fi'

import { formatDurationFromMilliseconds } from '~/util/date'
import { api, authHeaders } from '~/services/api'
import { GlobalContext } from '~/contexts/GlobalContext'

const timeScale = 0.25 // mínimo: 0.25

const syncBaseTimeout = Math.trunc(60 * 1000 * timeScale)
const autoSyncBaseTimeInterval = Math.trunc(20 * 1000 * timeScale)
const timeToTryingAutoSync = 1000

export function Tracker() {
	const { pathname } = useLocation()

	const { sessionId } = useContext(GlobalContext)

	const [inactivityTimeout, setInactivityTimeout] = useState(60 * 1000 * timeScale)
	const [inactivityData, setInactivityData] = useState(null)
	const [syncTimeout, setSyncTimeout] = useState(syncBaseTimeout)
	const [autoSyncTimeout, setAutoSyncTimeout] = useState(autoSyncBaseTimeInterval)
	const [inactivityTime, setInactivityTime] = useState(0)
	const [effectiveInactivityTime, setEffectiveInactivityTime] = useState(0)
	const [timeInPath, setTimeInPath] = useState(0)

	const lastSyncDatetime = useRef(new Date())

	function onActivityDetected() {
		setInactivityTime(0)
		setSyncTimeout(syncBaseTimeout)
	}

	async function syncActivityData() {
		const storedInactivityDataString = localStorage.getItem('sgm-activities')

		if(!storedInactivityDataString) {
			return
		}

		const storedData = JSON.parse(storedInactivityDataString)

		const prevActivities = storedData.activities.slice(0, storedData.activities.length - 1)
		const lastActivity = storedData.activities.at(-1)

		if(!lastActivity) {
			return
		}

		lastActivity.timeInPath = timeInPath
		lastActivity.inactivityTime = effectiveInactivityTime
		lastActivity.activityTime = timeInPath - effectiveInactivityTime

		const nonSyncronizedActivities = [
			...prevActivities.filter(item => !item.syncronized),
			lastActivity
		]

		if(nonSyncronizedActivities.length) {
			try {
				await api.post('usuario/log_atividades', {
					...storedData,
					activities: nonSyncronizedActivities
				}, authHeaders())

				const updatedInactivityData = {
					...storedData,
					activities: storedData.activities.map(act => ({
						...act,
						syncronized: true
					}))
				}
			
				setInactivityData(updatedInactivityData)

				lastSyncDatetime.current = new Date()
			} catch(e) {
				toast.error(e?.message || e?.msg || 'Erro relacionado ao monitoramento de atividades.')
			}
		}
	}

	async function loadParameto() {
		try {
			const { valor } = await api.get('parametro/tolerancia_inatividade', authHeaders())

			setInactivityTimeout(+valor * timeScale)
		} catch(e) {
			toast.error(e.msg)
		}
	}

	useEffect(() => {
		setTimeout(loadParameto, 800)
	}, [])
	
	useEffect(() => {
		if(sessionId) {
			const storedInactivityDataString = localStorage.getItem('sgm-activities')

			if(storedInactivityDataString) {
				localStorage.removeItem('sgm-activities')
			}

			setInactivityData({
				session_id: sessionId,
				activities: []
			})
		}
	}, [sessionId])

	useEffect(() => {
		if(inactivityData) {
			setInactivityData(old => {
				const activities = old.activities.slice(0, old.activities.length - 1)

				const prevActivity = old.activities.at(-1)

				if(prevActivity) {
					prevActivity.timeInPath = timeInPath
					prevActivity.inactivityTime = effectiveInactivityTime
					prevActivity.activityTime = timeInPath - effectiveInactivityTime
					
					if(prevActivity.activityTime < 0) {
						prevActivity.activityTime = 0
					}

					activities.push(prevActivity)
				}

				if(prevActivity?.path === pathname) {
					return old
				}

				setTimeInPath(0)
				setEffectiveInactivityTime(0)

				return {
					...old,
					activities: [
						...activities,
						{
							path: pathname,
							datetime: new Date(),
							timeInPath: 0,
							inactivityTime: 0,
							activityTime: 0,
							syncronized: false
						}
					]
				}
			})
		}
	}, [pathname])

	useEffect(() => {
		window.addEventListener('click', onActivityDetected)
		window.addEventListener('keydown', onActivityDetected)
		window.addEventListener('focus', onActivityDetected)

		return () => {
			window.removeEventListener('click', onActivityDetected)
			window.removeEventListener('keydown', onActivityDetected)
			window.removeEventListener('focus', onActivityDetected)
		}
	}, [inactivityTime])

	useEffect(() => {
		const syncActivityDataInterval = setInterval(() => {
			if(inactivityTime > inactivityTimeout) {
				setSyncTimeout(old => old >= 1000 ? old - 1000 : 0)
			}

			setAutoSyncTimeout(old => old >= 1000 ? old - 1000 : autoSyncBaseTimeInterval)

			setInactivityTime(old => old + 1000)

			setTimeInPath(old => old + 1000)

			const inactivityTime2 = inactivityTime - inactivityTimeout

			if(inactivityTime2 > 0) {
				setEffectiveInactivityTime(old => old + 1000)
			}
		}, 1000)

		return () => {
			clearInterval(syncActivityDataInterval)
		}
	}, [inactivityTime])

	useEffect(() => {
		if(syncTimeout <= 0) {
			syncActivityData()

			setSyncTimeout(syncBaseTimeout)
		}
	}, [syncTimeout])

	useEffect(() => {
		if(autoSyncTimeout <= 0) {
			syncActivityData()

			setAutoSyncTimeout(autoSyncBaseTimeInterval)
		}
	}, [autoSyncTimeout])

	useEffect(() => {
		const autoSyncInterval = setInterval(() => {
			// Sincroniza com o servidor "autoSyncBaseTimeInterval" milissegundos após a última sincronização
			if(differenceInSeconds(new Date(), lastSyncDatetime.current) > autoSyncBaseTimeInterval / 1000) {
				syncActivityData()
			}
		}, timeToTryingAutoSync) // Verifica a cada 1 segundo se deve sincronizar (com o servidor) as tarefas ainda não sincronizadas

		return () => {
			clearInterval(autoSyncInterval)
		}
	}, [])

	useEffect(() => {
		if(inactivityData) {
			localStorage.setItem('sgm-activities', JSON.stringify(inactivityData))
		}
	}, [inactivityData])

	const closedActivities = inactivityData?.activities ? inactivityData.activities.slice(0, inactivityData.activities.length - 1) : []
	const totalInactivityTime = closedActivities.reduce((total, current) => total + current.inactivityTime, 0) + effectiveInactivityTime

	return (
		<div 
			// style={{
			// 	boxShadow: '0 4px 5px 0 rgba(0,0,0,0.14),0 1px 10px 0 rgba(0,0,0,0.12),0 2px 4px -1px rgba(0,0,0,0.3)',
			// 	position: 'fixed',
			// 	bottom: 0,
			// 	width: '46rem',
			// 	margin: '16px 12px',
			// 	color: '#424242',
			// 	display: 'flex',
			// 	flexDirection: 'column',
			// 	gap: 8,
			// 	backgroundColor: 'gold',
			// 	padding: '16px',
			// 	borderRadius: '8px',
			// 	fontSize: '12px',
			// 	height: 'fit-content',
			// 	maxHeight: '85%',
			// 	maxWidth: '90%',
			// 	overflowY: 'auto',
			// 	marginLeft: '70px'
			// }}
			style={{
				display: 'none'
			}}
		>
			<h1 style={{ fontSize: 16, color: '#424242', fontWeight: 'bold', marginBottom: '6px' }}>Monitoramento de atividade</h1>

			<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
				<FiPower size={12} />
				{`Sessão: ${sessionId}`}
			</p>

			<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
				<FiLink size={12} />
				{`Local atual: ${window.location.pathname}`}
			</p>

			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<div style={{ display: 'flex', gap: 5 }}>
					<FiClock size={12} />

					{'Tempo de inatividade parcial: '}
					
					<span className={`${inactivityTime - inactivityTimeout > 0 ? 'bg-red-500 font-bold text-white p-1 rounded-md' : ''}`}>
						{`${formatDurationFromMilliseconds(inactivityTime)}`}
					</span>
				</div>

				<div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
					<FiAlertTriangle size={12} />

					{` (tolerância: ${inactivityTimeout / 1000} segundos de inatividade)`}
				</div>
			</div>

			<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
				<FiClock size={12} />
				{`Tempo de inatividade efetiva: ${formatDurationFromMilliseconds(totalInactivityTime)}`}
			</p>

			<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
				<FiRotateCw size={12} />
				{`Auto sincronização em: ${formatDurationFromMilliseconds(autoSyncTimeout)}`}
			</p>

			{syncTimeout < syncBaseTimeout && (
				<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
					<FiServer size={12} />
					{`Sincronizando por inatividade em: ${formatDurationFromMilliseconds(syncTimeout)}`}
				</p>
			)}

			<p style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
				<FiEye size={12} />
				Atividades:
			</p>

			{inactivityData && (
				<div style={{ borderRadius: '10px' }}>
					<table style={{ width: '100%', fontSize: '11px' }}>
						<thead style={{ backgroundColor: '#FFF5' }}>
							<tr>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px' }}>Local</th>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px', width: '62px' }}>Entrada</th>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px', width: '150px' }}>Tempo total</th>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px', width: '150px' }}>Tempo ativo</th>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px', width: '150px' }}>Tempo inativo</th>
								<th style={{ border: 'solid 1px #424242', padding: '4px 8px', width: '60px' }}>Sincronizado</th>
							</tr>
						</thead>
						<tbody>
							{inactivityData.activities.map((activity, idx, array) => {
								const isLast = idx === array.length - 1

								return (
									<tr key={`${activity.path}_${activity.datetime.toString()}`}>
										<td style={{ border: 'solid 1px #424242', padding: '4px 8px' }}>
											{activity.path === '/' ? 'login' : activity.path}
										</td>
										
										<td style={{ border: 'solid 1px #424242', padding: '4px 8px', textAlign: 'center' }}>
											{format(new Date(activity.datetime), 'HH:mm:ss')}
										</td>
										
										<td style={{ border: 'solid 1px #424242', padding: '4px 8px', textAlign: 'center' }}>
											{`${isLast 
												? formatDurationFromMilliseconds(timeInPath) 
												: formatDurationFromMilliseconds(activity.timeInPath)}
											`}
										</td>
										
										<td style={{ border: 'solid 1px #424242', padding: '4px 8px', textAlign: 'center' }}>
											{`${isLast
												? formatDurationFromMilliseconds(timeInPath - effectiveInactivityTime)
												: formatDurationFromMilliseconds(activity.timeInPath - activity.inactivityTime)
											}`}
										</td>
										
										<td style={{ border: 'solid 1px #424242', padding: '4px 8px', textAlign: 'center' }}>
											{`${isLast 
												? formatDurationFromMilliseconds(effectiveInactivityTime) 
												: formatDurationFromMilliseconds(activity.inactivityTime)}
											`}
										</td>

										<td style={{ border: 'solid 1px #424242', padding: '4px 8px', textAlign: 'center' }}>
											{activity.syncronized ? <FiCheckCircle color="green" size={14} /> : <FiXCircle color="red" size={15} />}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			)}

			<p 
				style={{
					display: 'grid',
					gridTemplateColumns: '22px auto',
					gap: '2px',
					alignItems: 'center',
					fontSize: '11px'
				}} 
			>
				<FiAlertTriangle size={17} />
				Jamais altere manualmente o horário do seu sistema. Isto afetará a contabilização das horas trabalhadas e poderá prejudicá-lo futuramente.
			</p>
		</div>
	)
}
